import {
    Button,
    FormField,
    Radiobox,
    ProcessStepper,
    BreadCrumb
} from '@weirgroup/weir-dls'
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { PositionType, SingleAssetGeofencesType } from '../../types/geofence.type'
import { GEOFENCE_SHAPE } from '../../constants/bucket.contants'
import { TagType } from '../../types/bucketTypes'
import GoogleApiWrapper, { GeofenceMapRef } from './components/geofence-map'
import { useTranslation } from 'react-i18next'
import geofenceApi from '../../api/geofence.api';
import { useGlobalError } from '../../contexts/global-error.context';
import './styles.scss'
import { useAnalyticsEvent } from '../../hooks/useAnalyticsEvent'
import { LoadingSpinner } from '../loading-spinner'
export type StepProps = {
  title?: string;
  state: 'active' | 'disabled' | 'success';
};

interface Props {
    setShowAdd: Dispatch<SetStateAction<boolean>>
    tagData: TagType
    selectedGeofence?: SingleAssetGeofencesType | null
    setSelectedGeofence?: Dispatch<SetStateAction<SingleAssetGeofencesType | null>>
    refreshList: () => void;
    setIsAssetUpdated: Dispatch<SetStateAction<boolean>>
    darkMode?: boolean
}

// Reusable Geofence Name Field
const GeofenceNameField = ({
    darkMode,
    value,
    onChange,
    t,
}: {
    darkMode?: boolean,
    value: string,
    onChange: (e: any) => void,
    t: any,
}) => (
    <FormField
        darkMode={darkMode}
        name="InputBox"
        label={t("geofenceForm.geofenceName")}
        disabled={false}
        size={'sm'}
        width={'100%'}
        placeholder={t("geofenceForm.geofenceName")}
        onChange={onChange}
        required
        value={value}
    />
);

export const AddGeofence = (props: Props) => {
    const logAnalyticsEvent = useAnalyticsEvent();
    const {
        setShowAdd,
        tagData,
        selectedGeofence,
        setSelectedGeofence,
        refreshList,
        setIsAssetUpdated,
        darkMode
    } = props
    const { t } = useTranslation();
    const { showError, showSuccess } = useGlobalError();
    const mapRef = useRef<GeofenceMapRef>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [geofenceName, setGeofenceName] = useState<string>('')
    const [geofenceRadius, setGeofenceRadius] = useState<number>(50)
    const [geofenceCoordinates, setGeofenceCoordinates] = useState<
        PositionType[]
    >([])
    const [step, setStep] = useState<any>([
        {
            state:"active",
            title: t("geofenceForm.name"),
        },
        {
            state:"disabled",
            title: t("geofenceForm.type"),
        },
        {
            state:"disabled",
            title: t("geofenceForm.location"),
        },
        {
            state:"disabled",
            title: t("geofenceForm.radius"),
        },
    ])
    
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
    // Track previous geofence id to avoid resetting stepper on every update
    const prevGeofenceIdRef = useRef<string | number | undefined>(undefined);
    
    // Handle step navigation - only allow going back to completed steps
    const handleStepClick = (clickedStep: StepProps, stepIndex: number) => {
        // Only allow navigation to previous steps (completed or current)
        if (stepIndex <= currentStepIndex) {
            // Update current step index
            setCurrentStepIndex(stepIndex);
            
            // Show appropriate tab
            setShowTab({
                name: stepIndex === 0,
                type: stepIndex === 1,
                location: stepIndex === 2,
                radius: stepIndex === 3 && selectedShape.circle,
            });
        }
    };

    // Helper function to advance to next step
    const advanceToNextStep = (currentIndex: number) => {
        const nextIndex = currentIndex + 1;
        setCurrentStepIndex(nextIndex);
    };

    // Show error message helper
    const showErrorMessage = (message: string) => {
        showError(message);
    };


    const [selectedShape, setSelectedShape] = useState({
        circle: true,
        polygon: false,
    })

    // Helper function to get step state
    const getStepState = (stepIndex: number): string => {
        if (currentStepIndex === stepIndex) return "active";
        if (currentStepIndex > stepIndex) return "success";
        return "disabled";
    };

    // Update steps when shape changes
    useEffect(() => {
        setStep([
            {
                state: getStepState(0),
                title: t("geofenceForm.name"),
            },
            {
                state: getStepState(1),
                title: t("geofenceForm.type"),
            },
            {
                state: getStepState(2),
                title: t("geofenceForm.location"),
            },
            ...(selectedShape.circle ? [{
                state: getStepState(3),
                title: t("geofenceForm.radius"),
            }] : [])
        ]);
    }, [selectedShape, currentStepIndex, t]);

    let geofenceType: GEOFENCE_SHAPE = selectedShape.polygon
        ? GEOFENCE_SHAPE.POLYGON
        : GEOFENCE_SHAPE.CIRCLE

    const assetCoordinates = useMemo(() => {
        if (tagData && typeof window !== 'undefined' && window.google && window.google.maps) {
            const asset = tagData;
            if (asset.location?.latitude && asset.location?.longitude) {
                const { latitude: lat, longitude: lng } = asset.location;
                const location = new window.google.maps.LatLng({ lat, lng });
                return location;
            }
        }
        return undefined;
    }, [tagData]);

    const [selectedLocation, setSelectedLocation] = useState({
        assetLocation: false,
        newLocation: false,
    })

    const [showTab, setShowTab] = useState({
        name: true,
        type: false,
        location: false,
        radius: false,
    })

    const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false)

    useEffect(() => {
        const checker = !geofenceCoordinates || !geofenceName || (selectedShape.circle && !geofenceRadius) || !geofenceType;
        setIsSubmitDisabled(checker);
    }, [selectedLocation, geofenceCoordinates, geofenceName, selectedShape, geofenceRadius, geofenceType])


    const changeShape = useCallback(
        (shape: GEOFENCE_SHAPE) => () => {
            setSelectedShape({
                circle: shape == GEOFENCE_SHAPE.CIRCLE,
                polygon: shape == GEOFENCE_SHAPE.POLYGON,
            })
            if (shape == GEOFENCE_SHAPE.POLYGON) {
                setSelectedLocation({
                    assetLocation: false,
                    newLocation: true,
                })
                // If we're currently on the radius step and switching to polygon, go back to location step
                if (currentStepIndex === 3) {
                    setCurrentStepIndex(2);
                    setShowTab({
                        name: false,
                        type: false,
                        location: true,
                        radius: false,
                    });
                }
            } else {
                setSelectedLocation({
                    assetLocation: false,
                    newLocation: false,
                })
            }
            setGeofenceCoordinates([])
        },
        [selectedShape, currentStepIndex]
    )

    useEffect(() => {
        if (selectedLocation.assetLocation) {
            const coords = [{
                order: 0,
                latitude: tagData.location.latitude,
                longitude: tagData.location.longitude
            }]
            setGeofenceCoordinates(coords)
        }
    }, [selectedLocation])

    useEffect(() => {
        if (selectedShape.polygon) {
            setSelectedLocation({
                assetLocation: false,
                newLocation: true,
            })
        }
    }, [selectedShape])

    useEffect(() => {
        if (selectedGeofence) {
            setGeofenceName(selectedGeofence.name)
            setGeofenceRadius(selectedGeofence.radius)
            geofenceType = selectedGeofence.shape == "circle" ? GEOFENCE_SHAPE.CIRCLE : GEOFENCE_SHAPE.POLYGON
            setGeofenceCoordinates(JSON.parse(selectedGeofence.coordinates.replaceAll('&quot;', '"')))
            setSelectedShape({
                circle: selectedGeofence.shape == "circle",
                polygon: selectedGeofence.shape == "polygon"
            })
            // Set appropriate location selection for circles
            if (selectedGeofence.shape == "circle") {
                const coords = JSON.parse(selectedGeofence.coordinates.replaceAll('&quot;', '"'));
                if (coords.length > 0 && tagData?.location) {
                    const isAssetLocation = coords[0].latitude === tagData.location.latitude && 
                                          coords[0].longitude === tagData.location.longitude;
                    setSelectedLocation({
                        assetLocation: isAssetLocation,
                        newLocation: !isAssetLocation,
                    });
                } else {
                    setSelectedLocation({
                        assetLocation: false,
                        newLocation: true,
                    });
                }
            } else {
                // For polygons, always use new location
                setSelectedLocation({
                    assetLocation: false,
                    newLocation: true,
                });
            }

            // Only reset stepper if a new geofence is selected (id changed)
            const currentId = selectedGeofence.geofence_id;
            if (prevGeofenceIdRef.current !== currentId) {
                setCurrentStepIndex(0);
                setShowTab({
                    name: true,
                    type: false,
                    location: false,
                    radius: false,
                });
                prevGeofenceIdRef.current = currentId;
            }
        } else {
            prevGeofenceIdRef.current = undefined;
        }
    }, [selectedGeofence, tagData])

    // Refactored nameTab
    const nameTab = (index: any) =>{
        return(
            <div className="tab-content">
                <div className="content-wrapper">
                    <div className="name-type">
                        <div style={{marginTop:10}}></div>
                        <GeofenceNameField
                            darkMode={darkMode}
                            value={geofenceName}
                            onChange={(e: any) => setGeofenceName(e.target.value)}
                            t={t}
                        />
                    </div>
                </div>
                <div className='size-box'></div>
                <div className="btn-container">
                    <Button
                        darkMode={darkMode}
                        label={t("geofenceForm.next")}
                        size={'sm'}
                        variant={'primary'}
                        onClick={() => {
                            if (geofenceName.trim().length != 0) {
                                advanceToNextStep(index);
                                setShowTab({
                                    name: false,
                                    type: true,
                                    location: false,
                                    radius: false,
                                })
                            } else {
                                showErrorMessage(t("validGeofenceNameMessage"))
                            }
                        }}
                        disabled={
                            geofenceName.length == 0 ||
                            (!selectedShape.circle && !selectedShape.polygon)
                        }
                    />
                </div>
            </div>) 
    }

    // Refactored typeTab
    const typeTab = (index:any) => {
        return(
            <div className="tab-content">
                <div className="content-wrapper">
                    <div className="name-type">
                        <div className="shape">
                            <p className="title">{t("geofenceForm.selectShape")}</p>
                            <div className="radio-btn">
                                <Radiobox
                                    darkMode={darkMode}
                                    name="circle"
                                    label={t("geofenceForm.circle")}
                                    value={'circle'}
                                    checked={selectedShape.circle}
                                    disabled={selectedGeofence}
                                    onChange={changeShape(GEOFENCE_SHAPE.CIRCLE)}
                                />
                                <Radiobox
                                    darkMode={darkMode}
                                    name="polygon"
                                    label={t("geofenceForm.polygon")}
                                    value={'polygon'}
                                    checked={selectedShape.polygon}
                                    disabled={selectedGeofence}
                                    onChange={changeShape(GEOFENCE_SHAPE.POLYGON)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='size-box'></div>
                <div className="btn-container">
                    <Button
                        darkMode={darkMode}
                        label={t("geofenceForm.next")}
                        size={'sm'}
                        variant={'primary'}
                        onClick={() => {
                            if (geofenceName.trim().length != 0) {
                                advanceToNextStep(index);
                                setShowTab({
                                    name: false,
                                    type: false,
                                    location: true,
                                    radius: false,
                                })
                            } else {
                                showErrorMessage(t("validGeofenceNameMessage"))
                            }
                        }}
                        disabled={
                            geofenceName.length == 0 ||
                            (!selectedShape.circle && !selectedShape.polygon)
                        }
                    />
                </div>
            </div>
        )
    }

    const renderSpinner = () => {
        return (
            <LoadingSpinner darkMode={darkMode} />
        )
    }

    const locationTab = (index: any) => {
        return (
            <div className="tab-content">
                <div className="content-wrapper">
                    <div className="location-container">
                        {/* Show map for polygon or when circle with new location is selected */}
                        {(selectedShape.polygon || (selectedShape.circle && selectedLocation.newLocation) || (selectedGeofence?.coordinates)) ? (
                            <div className="map-container">
                                <div className="geofence-map">
                                    <GoogleApiWrapper
                                        ref={mapRef}
                                        radius={geofenceRadius}
                                        geofenceType={geofenceType}
                                        assetCoordinates={assetCoordinates}
                                        geofenceCoordinates={geofenceCoordinates}
                                        setGeofenceCoordinates={setGeofenceCoordinates}
                                        selectedGeofence={selectedGeofence}
                                    />
                                </div>
                            </div>
                        ) : null}
                        {(selectedShape.circle && !selectedGeofence) ? (
                            <div className="radio-btn">
                                <Radiobox
                                    darkMode={darkMode}
                                    name="Asset Location"
                                    label={t("geofenceForm.assetLocation")}
                                    value={'Asset Location'}
                                    checked={selectedLocation.assetLocation}
                                    disabled={assetCoordinates == undefined}
                                    onChange={() => {
                                        setSelectedLocation({
                                            assetLocation: true,
                                            newLocation: false,
                                        })
                                    }}

                                />
                                <Radiobox
                                    darkMode={darkMode}
                                    name="New Location"
                                    label={t("geofenceForm.newLocation")}
                                    value={'New Location'}
                                    checked={selectedLocation.newLocation}
                                    disabled={false}
                                    onChange={() => {
                                        setSelectedLocation({
                                            assetLocation: false,
                                            newLocation: true,
                                        })
                                    }}
                                />

                            </div>
                        ) : (
                            <></>
                        )}
                        {isLoading && selectedShape.polygon ? renderSpinner() : <></>}
                    </div>
                </div>
                <div className='size-box'></div>
                <div className="btn-container">
                    {selectedShape.polygon && (
                        <>
                            <Button
                                darkMode={darkMode}
                                label={t("geofenceForm.deleteLastPoint")}
                                size={'sm'}
                                variant={'primary'}
                                onClick={() => mapRef.current?.removeLastPoint()}
                            />
                            <div style={{margin: '10px'}}></div>
                            <Button
                                darkMode={darkMode}
                                label={t("geofenceForm.submit")}
                                disabled={isSubmitDisabled || geofenceCoordinates.length < 3 || isLoading}
                                size={'sm'}
                                variant={'primary'}
                                onClick={() => submitGeofence(index)}
                            />
                        </>
                    )}
                    {selectedShape.circle && (
                        <Button
                            darkMode={darkMode}
                            label={t("geofenceForm.next")}
                            size={'sm'}
                            variant={'primary'}
                            onClick={() => {
                                advanceToNextStep(index);
                                setShowTab({
                                    name: false,
                                    type: false,
                                    location: false,
                                    radius: true,
                                })
                            }}
                            disabled={
                                selectedGeofence ? isSubmitDisabled : (!selectedLocation.assetLocation && !selectedLocation.newLocation)
                            }
                        />
                    )}
                </div>
            </div>
        )
    }

    const radiusTab = (index:any) => {
        return (
            <div className="tab-content">
                <div className="content-wrapper">
                    <div className="location-container">
                        {isLoading ? renderSpinner() : <></>}
                        <div style={{marginTop:10, marginBottom:10}}></div>
                        <FormField
                            darkMode={darkMode}
                            name="InputBox"
                            required={true}
                            label={t("geofenceForm.selectRadius")}
                            type="number"
                            min="1"
                            size="sm"
                            width="100%"
                            placeholder={t("geofenceForm.makeSelection")}
                            onChange={(e: any) => {
                                if (e.target.value >= 0) {
                                    setGeofenceRadius(e.target.value);
                                }
                            }}
                            onKeyPress={(e: any) => {
                                // Prevent invalid characters like 'e', 'E', '+', '-', and '.'
                                if (["e", "E", "+", "-", "."].includes(e.key)) {
                                    e.preventDefault();
                                }
                            }}
                            value={geofenceRadius}
                        />
                    </div>
                </div>
                <div className='size-box'></div>
                <div className="btn-container">
                    <Button
                        darkMode={darkMode}
                        label={t("geofenceForm.submit")}
                        disabled={isSubmitDisabled || isLoading}
                        size={'sm'}
                        variant={'primary'}
                        onClick={() => submitGeofence(index)}
                    />
                </div>
            </div>
        )
    }

    const isCompleteData = (data: any) => {
        const checker = data.name && data.shape && data.coordinates !== "[]"
        if (selectedShape.circle) {
            return checker && data.radius;
        } else {
            return checker
        }
    }

    const submitGeofence = async (index:any) => {
        setIsLoading(true);
        const data = {
            asset_id: tagData.asset_id,
            coordinates: btoa(JSON.stringify(geofenceCoordinates)),
            name: geofenceName,
            radius: geofenceRadius,
            shape: geofenceType,
            geofence_id: selectedGeofence?.geofence_id
        }
        if(geofenceRadius < 0 || geofenceRadius > 4000000){
            showError(t("radiusValidationError", "The radius must be between 0 and 4,000,000."), t("geofenceValidationError", "Geofence Validation Error"), "warning");
            setIsLoading(false)
        }
        else if (isCompleteData(data)) {
            const apiData = selectedGeofence ? { data, editGeofence: true } : { data }
            try {
                await geofenceApi.saveGeofence(apiData);
                // Wait for refreshList to complete before closing modal
                await Promise.resolve(refreshList());
                setIsLoading(false);
                setShowAdd(false);
                setIsAssetUpdated(true);
                if (setSelectedGeofence) {
                    setSelectedGeofence(null);
                }
                // Show appropriate success message for create vs update
                const successMessage = selectedGeofence 
                    ? t("geofenceUpdatedSuccess", "Geofence updated successfully")
                    : t("geofenceCreatedSuccess", "Geofence created successfully");
                showSuccess(successMessage);
                if(selectedGeofence)
                    logAnalyticsEvent('web_user_edited_geofence', { description: 'User edited a geofence' });
                else
                    logAnalyticsEvent('web_user_added_geofence', { description: 'User added a geofence' });
            } catch (err: any) {
                // Global error handling will show the error automatically
                // But we can add specific error display for validation errors
                if (err.response?.data?.message) {
                    showError(err.response.data.message, t("geofenceError", "Geofence Error"));
                }
                setIsLoading(false);
            }
        } else {
            showError(t("submitGeofenceErrorMessage", "Please fill in all required fields"));
            setIsLoading(false);
        }
    }
    
    const menuItems: string[] = [
        t("geofenceList"),
        selectedGeofence ? selectedGeofence.name : t("addGeofence")
    ];
    
    return (
        <div className="geofence-container">
            <div className="geofence-header">
                <BreadCrumb
                    darkMode={darkMode}
                    menuItems={menuItems}
                    onItemClick={(clickedItem: string, index?: number) => {
                        // Only navigate back to list when clicking "Geofence List"
                        if (clickedItem === t("geofenceList") || index === 0) {
                            setShowAdd(false);
                            if (setSelectedGeofence) {
                                setSelectedGeofence(null);
                            }
                        }
                    }}
                />
            </div>
            
            <div className="geofence-content">
                <div className="milestone-nav">
                <ProcessStepper 
                    modalType="small"
                    steps={step}
                    onStepClick={handleStepClick}
                />
                </div>
                {showTab.name ? nameTab(0) : <></>}
                {showTab.type ? typeTab(1) : <></>}
                {showTab.location ? locationTab(2) : <></>}
                {showTab.radius && selectedShape.circle ? radiusTab(3) : <></>}
            </div>
        </div>
    )
}